import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { FreddySeal, FreddyWordmark } from "../components/FreddyLogo";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, loginEmail } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("corp@ourisman.demo");
  const [password, setPassword] = useState("freddy2026");
  const [loading, setLoading] = useState(false);

  if (user) {
    const dest = user.role === "corporate" ? "/corporate" : `/rooftop/${user.assigned_rooftop_id}`;
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await loginEmail(email, password);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "corporate" ? "/corporate" : `/rooftop/${u.assigned_rooftop_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/corporate";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative grain flex flex-col items-center justify-center p-6"
         style={{ background: "radial-gradient(ellipse at top, #0F1544 0%, #090E33 40%, #040617 100%)" }}>
      <div className="absolute top-6 left-6">
        <FreddyWordmark variant="white" height={26} showTagline={false} />
      </div>

      <div className="w-full max-w-[440px] bg-white rounded-xl p-8 shadow-2xl relative z-10"
           data-testid="login-card">
        <div className="flex flex-col items-center text-center mb-6">
          <FreddySeal size={96} />
          <h1 className="mt-5 text-[24px] font-semibold text-navy leading-tight">
            Freddy Buy Signal
          </h1>
          <p className="mt-1 text-sm text-navy/60 font-medium">
            for Ourisman Automotive Group
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 block mb-1">
              Email
            </label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-11 px-3 rounded-md border border-slate-300 focus:border-navy focus:ring-2 focus:ring-navy/10 outline-none text-sm font-medium"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 block mb-1">
              Password
            </label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full h-11 px-3 rounded-md border border-slate-300 focus:border-navy focus:ring-2 focus:ring-navy/10 outline-none text-sm font-medium"
            />
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-freddy-red hover:bg-freddy-redDark text-white font-bold uppercase tracking-wider text-sm rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Sign in
          </button>
        </form>

        <div className="flex items-center gap-3 my-4">
          <div className="h-px bg-slate-200 flex-1" />
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">or</span>
          <div className="h-px bg-slate-200 flex-1" />
        </div>

        <button
          data-testid="google-login"
          onClick={googleLogin}
          className="w-full h-11 bg-white border border-slate-300 hover:bg-slate-50 text-navy font-semibold text-sm rounded-md transition-colors flex items-center justify-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="mt-5 rounded-md bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 font-medium">
          <span className="font-bold text-navy">Demo logins:</span> corp@ourisman.demo · gm@ourisman.demo · ucm@ourisman.demo · buyer@ourisman.demo ·{" "}
          <span className="text-slate-500">password: freddy2026</span>
        </div>
      </div>

      <div className="relative z-10 mt-6 text-[11px] text-white/60 font-semibold">
        Built by Freddy Media · Jacksonville FL
      </div>
    </div>
  );
}
